import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '../../services/api';

// PDF.js renders each page onto a <canvas> — pure JS, no browser PDF viewer and
// no navigation — so it works inside HubSpot's *sandboxed* iframe modal, where
// native PDF embedding (iframe/embed of a blob, data:, or Stripe URL) is blocked
// by Chrome. The worker is bundled and served same-origin by Vite.
//
// The dashboard isn't sandboxed and could embed natively, but a second
// rendering path would be gratuitous divergence for no gain.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface InvoicePdfViewProps {
  invoiceId: string;
  /** Max height of the scroll area. Smaller in the compact/iframe layout. */
  maxHeight?: string;
}

/**
 * A Stripe invoice PDF, rendered inline.
 *
 * We show Stripe's own PDF rather than reconstructing the invoice ourselves —
 * it is always correct and needs no schema upkeep. The bytes come from our
 * backend proxy, because Stripe's URL sets X-Frame-Options and serves as an
 * attachment, neither of which the browser will render for us.
 */
const InvoicePdfView: React.FC<InvoicePdfViewProps> = ({
  invoiceId,
  maxHeight = '600px',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    (async () => {
      try {
        const blob = await apiClient.getInvoicePdfBlob(invoiceId);
        const data = await blob.arrayBuffer();
        if (cancelled) return;

        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const container = containerRef.current;
        if (!container || cancelled) return;
        container.replaceChildren();

        for (let n = 1; n <= pdf.numPages; n++) {
          const page = await pdf.getPage(n);
          if (cancelled) return;
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.className = 'rounded-md border mb-2 bg-white';
          container.appendChild(canvas);
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, canvas, viewport }).promise;
          if (cancelled) return;
        }

        if (!cancelled) setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [invoiceId, attempt]);

  return (
    <div className="space-y-2">
      {status === 'loading' && (
        <div className="flex items-center justify-center gap-2 py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading invoice…</span>
        </div>
      )}

      {status === 'error' && (
        <div className="flex items-center gap-2 py-2">
          <span className="text-sm text-muted-foreground">
            Couldn't load the invoice PDF.
          </span>
          <Button variant="outline" size="sm" onClick={() => setAttempt((a) => a + 1)}>
            Retry
          </Button>
        </div>
      )}

      <div
        ref={containerRef}
        className="overflow-y-auto"
        style={{ maxHeight, display: status === 'ready' ? 'block' : 'none' }}
      />
    </div>
  );
};

export default InvoicePdfView;
